import type { BtfFormValues } from '@amzn/AmazonJourneysVueApp/interop/campaigns/btfV2/btf-v2.types';
import type { CommonFormVals } from '@amzn/AmazonJourneysVueApp/interop/campaigns/campaigns.types';
import type { GwFormValues } from '@amzn/AmazonJourneysVueApp/interop/campaigns/gateway/gateway.types';
import type { QacFormValues } from '@amzn/AmazonJourneysVueApp/interop/campaigns/qac/qac.types';
import type { QicFormValues } from '@amzn/AmazonJourneysVueApp/interop/campaigns/qic/qic.types';
import type { SacFormValues } from '@amzn/AmazonJourneysVueApp/interop/campaigns/sac/sac.types';
import type { SicFormValues } from '@amzn/AmazonJourneysVueApp/interop/campaigns/sic/sic.types';
import { eventShortNameOptions, languageOptions, PAGE_TYPE, PLATFORMS, PlatformNaming, ReasonNaming } from '@amzn/journeys-common';
import { fromPairs, isEmpty, keys, reduce, times } from 'es-toolkit/compat';
import { DestinationUrlInput, separator } from 'features/campaign/campaign.types';
import { ARRAY_ERROR, type FieldState, type FieldValidator } from 'final-form';
import type { Calculation } from 'final-form-calculate';

/**
 * A validator function for a field to make it a required field. For strings, checks if the string is empty.
 * For numbers, checks NaN. Otherwise considers non-nullish values to be present.
 * @param val
 */
export const required = (val: unknown) => {
  if ((typeof val === 'string' && isEmpty(val)) || (typeof val === 'number' && Number.isNaN(val)) || val === null || val === undefined) {
    return 'Required';
  }
};

/**
 * A validator function for a field that checks if a string's length is within an (inclusive) character length range.
 * @param {string} val  - The value to check the character length.
 * @param {number} min  - The minimum character count that `val` should have.
 * @param {number} max  - The maximum character count that `val` should have.
 * @returns {string | undefined} - Returns a string error message if the value is not in range. Otherwise, returns undefined.
 */
export function checkStringLength(val: string, min: number, max: number): string | undefined {
  const validationMsg = `Must be between ${min.toString()} and ${max.toString()} characters long.`;
  const lessThanMinMsg = `Must be at least ${min.toString()} characters long`;
  const moreThanMaxMsg = `Cannot be longer than ${max.toString()} characters long`;

  if (val) {
    const valLength = val.length;
    if (valLength < min && valLength > max) {
      return validationMsg;
    }
    if (valLength < min) {
      return lessThanMinMsg;
    }
    if (valLength > max) {
      return moreThanMaxMsg;
    }
  }
}
/**
 * A validator function for a numeric value entered in that must conform to a certain range, inclusive.
 * @param val Value to check
 * @param min Minimum value in range
 * @param max Maximum value in range
 */

// biome-ignore lint/suspicious/noExplicitAny: legacy code
export function checkRange(val: any, min: number, max: number): string | undefined {
  const validationMsg = `Must be a whole number between ${min.toString()} and ${max.toString()}`;
  // NOTE: empty form values (defaulted to undefined) are allowed because fields can be optional.
  if (Number.isInteger(Number.parseFloat(val))) {
    if (val < min || val > max) {
      return validationMsg;
    }
  } else if (!isEmpty(val)) {
    return validationMsg;
  }
}

// biome-ignore lint/suspicious/noExplicitAny: legacy code
export const updateCampaignNameObj: Calculation<any> = {
  // Automatic Campaign naming on change of below fields
  field: /isAutoGenerateCreativeName|platform|reason|eventShortName|productLine|program|alexaEnabled|language|additionalName/,
  updates: (
    // biome-ignore lint/suspicious/noExplicitAny: legacy code
    _currentValue: any,
    _fieldName: string,
    allValues: GwFormValues | BtfFormValues | SacFormValues | QicFormValues | SicFormValues,
    prevValues: GwFormValues | BtfFormValues | SacFormValues | QicFormValues | SicFormValues,
  ) => {
    if (isEmpty(prevValues)) {
      return {};
    }
    // get the index from string like areas[0].browseNode by replacing all characters expect the numbers
    const {
      title,
      isAutoGenerateCreativeName,
      reason,
      placements,
      eventShortName,
      productLine,
      program,
      alexaEnabled,
      language,
      additionalName,
    } = allValues;
    const platform = placements?.[0].platform;
    let creativeTitle = '';
    if (!isAutoGenerateCreativeName) {
      return {
        platform,
        reason,
        title,
      };
    }
    // language converter say from US English to EN with the help of languageOptions
    const locale = languageOptions.find((element) => element?.display === language)?.locale;
    // Event shortname to its value
    const eventShortNameObj = eventShortNameOptions.find((ele) => ele.display === eventShortName)?.value ?? eventShortName;
    // Creating an object for all the values we need to name the campaign
    const nameCreationObj = {
      additionalName,
      alexaEnabled,
      eventShortName: eventShortNameObj,
      language: locale,
      // biome-ignore lint/suspicious/noExplicitAny: legacy code
      platform: (PlatformNaming as any)[platform],
      productLine,
      program,
      // biome-ignore lint/suspicious/noExplicitAny: legacy code
      reason: (ReasonNaming as any)[reason],
    };
    const params = keys(nameCreationObj) as Array<keyof typeof nameCreationObj>;
    const compiledCreativeName =
      params.reduce((accumulator, currentValue) => {
        const name = nameCreationObj[currentValue] ? nameCreationObj[currentValue] : '';
        const separatorAddition = name ? separator : '';
        return accumulator + name + separatorAddition;
      }, '') ?? '';
    // Edge case - As it begins with Alexa enabled option in form, clearing the last underscore
    if (compiledCreativeName.charAt(compiledCreativeName.length - 1) === separator) {
      creativeTitle = compiledCreativeName.slice(0, compiledCreativeName.length - 1);
    }
    return {
      platform,
      reason,
      title: creativeTitle,
    };
  },
};

/**
 * update the destination url based on the link type selected
 *
 * @param { destinationUrl, browseNode, asin, linkType, refTag, platform }
 *
 * @returns {destinationUrl, browseNode, asin}
 */
export function getUpdatedDestinationUrl({
  destinationUrl,
  browseNode,
  asin,
  linkType,
  refTag,
  platform,
}: {
  destinationUrl: string | null;
  browseNode: string | null;
  asin: string | null;
  linkType: DestinationUrlInput | null;
  refTag: string | null;
  platform?: string | null;
}): string | null {
  // URL format reference: https://w.amazon.com/bin/view/Cross-Channel-Marketing/Onsite/Homepage/FAQ#HHowdoIformatmydestinationURL3F
  if (linkType === DestinationUrlInput.ASIN) {
    let isMobile: boolean;
    switch (platform) {
      case PLATFORMS.DESKTOP:
        isMobile = false;
        break;
      case PLATFORMS.MOBILE:
      case PLATFORMS.TABLET:
      case PLATFORMS.MOBILE_ROTATOR_H1:
      case PLATFORMS.MOBILE_ROTATOR_H4:
      case PLATFORMS.MOBILE_DASHBOARD_IMAGE_CARD:
      case PLATFORMS.MOBILE_TALL_HERO:
        isMobile = true;
        break;
      default:
        isMobile = false;
    }
    const destUrlFirstPart = isMobile ? `/gp/aw/d/${asin}` : `/dp/${asin}`;
    const destUrlSecondPart = refTag ? `/ref=${refTag}` : '';
    return destUrlFirstPart + destUrlSecondPart;
  }
  if (linkType === DestinationUrlInput.BROWSE_NODE) {
    return `/gp/browse.html?node=${browseNode}&ref_=${refTag}`;
  }
  return destinationUrl;
}

/**
 * when there is change in the browseNode or linkType or asin update the destinationUrl for additionalLink
 * @param destinationUrlName
 */

// biome-ignore lint/suspicious/noExplicitAny: legacy code
export const updateAdditionalLinkUrl: Calculation<any> = {
  field: /additionalLinkType|additionalLinkBrowseNode|additionalLinkAsin|additionalLinkRefTag|placements\[0\].platform/,
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  updates: (_currentValue: any, _fieldName: string, allValues: SacFormValues | QacFormValues, prevValues: GwFormValues) => {
    if (isEmpty(prevValues)) {
      return {};
    }
    const { additionalLinkType, additionalLinkBrowseNode, additionalLinkAsin, additionalLinkRefTag, secondaryViewLinkUrl, placements } =
      allValues;
    // Default to Desktop for non-Gateway campaigns.
    const platform =
      !isEmpty(placements) && placements[0]?.pageType === PAGE_TYPE.GATEWAY
        ? // biome-ignore lint/suspicious/noExplicitAny: legacy code
          (PLATFORMS as any)[placements[0].platform]
        : PLATFORMS.DESKTOP;

    const updatedDestinationUrl = getUpdatedDestinationUrl({
      asin: additionalLinkAsin,
      browseNode: additionalLinkBrowseNode ?? null,
      destinationUrl: secondaryViewLinkUrl,
      linkType: additionalLinkType as DestinationUrlInput | null,
      platform,
      refTag: additionalLinkRefTag,
    });
    return {
      additionalLinkAsin,
      additionalLinkBrowseNode,
      secondaryViewLinkUrl: updatedDestinationUrl,
    };
  },
};

/**
 * clears the languages selected in all placements
 * @param numPlacements
 * @param newLanguage
 */
export function changeDefaultLanguagesOnFormUpdate(numPlacements: number, newLanguage: string) {
  return fromPairs(
    times(numPlacements, (n) => [
      `placements[${n}].languages`,
      [
        newLanguage,
      ],
    ]),
  );
}

/**
 * Clear the language on placement if realm changes.
 */

// biome-ignore lint/suspicious/noExplicitAny: legacy code
export const setPlacementLanguageOnMarketplaceUpdate: Calculation<any> = {
  field: 'language',
  updates: (_language, _fieldName: string, allValues: CommonFormVals, prevValues: CommonFormVals) => {
    if (isEmpty(prevValues)) {
      return {};
    }

    const previousLanguageMarketplace = prevValues.language;
    const newLanguageMarketplace = allValues.language;

    if (previousLanguageMarketplace !== newLanguageMarketplace) {
      // If language selection changes for content set default placement language
      const newLanguage = allValues.language?.substring(2); // US English -> English;
      const placementLanguageUpdates = changeDefaultLanguagesOnFormUpdate(allValues.placements.length, newLanguage);
      return {
        ...placementLanguageUpdates,
      };
    }

    return {};
  },
};

// biome-ignore lint/suspicious/noExplicitAny: legacy code
export type MultiFieldValidatorFn = (value: any, allValues: any, meta?: FieldState<any>) => string[];
// biome-ignore lint/suspicious/noExplicitAny: legacy code
export type FieldValidatorFn = (value: any, allValues: any, meta?: FieldState<any>) => string | undefined;

export const emptyFieldValidator: MultiFieldValidatorFn = () => [];

/**
 * Wrap a validator function to make it return arrays instead of string/undefined
 * @param validator
 */
export function liftToMulti(validator: FieldValidatorFn): MultiFieldValidatorFn {
  return (value, allValues, meta) => {
    const result = validator(value, allValues, meta);
    return result === undefined
      ? []
      : [
          result,
        ];
  };
}

/**
 * Combine two field validators into one. The one will concat both the validators error messages into one.
 * @param v1
 * @param v2
 */
export function combineFieldValidators(v1: MultiFieldValidatorFn, v2: MultiFieldValidatorFn): MultiFieldValidatorFn {
  return (value, allValues, meta) => {
    const resultV1 = v1(value, allValues, meta);
    const resultV2 = v2(value, allValues, meta);

    return resultV1.concat(resultV2);
  };
}

// biome-ignore lint/suspicious/noExplicitAny: legacy code
export type FinalFormArrayError = any[];
// biome-ignore lint/suspicious/noExplicitAny: legacy code
export type FinalFormFieldValidatorFn = (value: any, allValues: any, meta: FieldState<any>) => string | undefined | FinalFormArrayError;

/**
 * Combine an array of validators into one that spits out any error messages in a space-separated list
 * @param isArrayField If the validators are for an array field
 * @param validators
 */

// biome-ignore lint/suspicious/noExplicitAny: legacy code
export function foldFieldValidators(isArrayField: boolean, validators: FieldValidatorFn[]): FieldValidator<any> {
  const multiValidator = reduce(validators.map(liftToMulti), combineFieldValidators, emptyFieldValidator);
  return (val, allValues, meta) => {
    const errors = multiValidator(val, allValues, meta);

    if (isEmpty(errors)) {
      return;
    }
    const aggregateErrorMessage = errors.join(' ');

    // final form expects special error format for array fields. See https://github.com/final-form/final-form/pull/92
    if (isArrayField) {
      // biome-ignore lint/suspicious/noExplicitAny: legacy code
      const arrayError: any = [];
      arrayError[ARRAY_ERROR] = aggregateErrorMessage;
      return arrayError;
    }
    return aggregateErrorMessage;
  };
}
